import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import Menu from '@/global/components/MenuV2'
import { useFilterLocaisCentroStore } from '@/global/components/Selects'
import { usePeriodo } from '@/global/components/Selects/SelectPeriodo'
import ContentGuide from '@/global/guides/ContentGuide'
import { TrocaAutomaticaTabela, trocaHorario } from '@/service/api'
import { useStore } from '@/store'
import { calcularRepeticao } from '@/templates/ManutencaoDoPonto/TrocaAutomaticaHorario/components/accordion'
import dayjs from 'dayjs'
import SP from 'global/components/Selects/SelectPeriodo'
import { CalendarIcon, PencilIcon, Trash2Icon } from 'lucide-react'
import { Outlet, useOutletContext } from 'react-router-dom'

const columns: GridColDefPro<TrocaAutomaticaTabela>[] = [
    {
        field: 'Funcionario',
        headerName: 'Funcionário',
        minWidth: 200,
        flex: 0.8,
        valueGetter: params => params.row.Funcionario?.nome || '',
    },
    {
        field: 'TabelaHorario',
        headerName: 'Tabela de horário',
        minWidth: 200,
        flex: 0.6,
        valueGetter: params => params.row.TabelaHorario?.nome || '',
    },
    {
        field: 'dataInicial',
        headerName: 'A partir de',
        width: 150,
        valueGetter: ({ value }) => dayjs.utc(value).format('DD/MM/YYYY'),
    },
    {
        field: 'dataFinal',
        headerName: 'Até',
        width: 150,
        valueGetter: ({ value }) =>
            value ? dayjs.utc(value).format('DD/MM/YYYY') : value,
        valueFormatter: ({ value }) => value || 'Indeterminado',
    },
    {
        field: 'repetir',
        headerName: 'Vai se repetir',
        minWidth: 300,
        flex: 1,
        valueGetter: ({
            row: {
                tipoRepeticao,
                repeticaoMensalOpcao,
                every,
                dataInicial,
                repeticaoSemanalDia,
            },
        }) => {
            const everyN = every ?? 0

            if (tipoRepeticao === 'DIARIO')
                return everyN < 1 ? 'Todos os dias' : `A cada ${everyN} dia(s)`

            if (tipoRepeticao === 'SEMANAL')
                return everyN < 1
                    ? `Todas as semanas, no(a) ${dayjs()
                          .day(repeticaoSemanalDia)
                          .format('dddd')}`
                    : `A cada ${
                          everyN < 2 ? 'uma semana' : `${everyN} semanas`
                      }, no(a) ${dayjs()
                          .day(repeticaoSemanalDia)
                          .format('dddd')}`

            if (repeticaoMensalOpcao === 'DIA')
                return `Todo dia ${dayjs.utc(dataInicial).date()}, ${
                    everyN < 1
                        ? 'todo mês'
                        : `a cada ${everyN} mês${everyN < 2 ? '' : 'es'}`
                }`

            const { artigo, diaSemana, ordinal, semana } =
                calcularRepeticao(dataInicial)

            return `Tod${artigo} ${semana}${ordinal} ${diaSemana}, ${
                everyN < 1
                    ? 'todo mês'
                    : `a cada ${everyN} mês${everyN < 2 ? '' : 'es'}`
            }`
        },
    },
    {
        field: 'duracaoRepeticao',
        headerName: 'Vai durar',
        minWidth: 200,
        flex: 1,
        valueGetter: ({ row }) => {
            const { duracaoRepeticao, tipoDuracaoRepeticao } = row
            switch (tipoDuracaoRepeticao) {
                case 'DIARIO':
                    return `${
                        duracaoRepeticao < 2 ? 'um' : duracaoRepeticao
                    } dia${duracaoRepeticao < 2 ? '' : 's'}`
                case 'SEMANAL':
                    return `${
                        duracaoRepeticao < 2 ? 'uma' : duracaoRepeticao
                    } semana${duracaoRepeticao < 2 ? '' : 's'}`
                case 'MENSAL':
                    return `${
                        duracaoRepeticao < 2 ? 'um' : duracaoRepeticao
                    } mês${duracaoRepeticao < 2 ? '' : 'es'}`
                default:
                    return ''
            }
        },
    },
    {
        ...optionsColumn,
        field: '__actions',
        type: 'actions',
        getActions: ({ id }) => [
            <Menu.Item key="visualizar" href={`${id}/preview`}>
                <Menu.Item.Icon>
                    <CalendarIcon />
                </Menu.Item.Icon>
                <Menu.Item.Text>Visualizar</Menu.Item.Text>
            </Menu.Item>,
            <Menu.Item key="editar" href={`${id}/editar`}>
                <Menu.Item.Icon>
                    <PencilIcon />
                </Menu.Item.Icon>
                <Menu.Item.Text>Editar</Menu.Item.Text>
            </Menu.Item>,
            <Menu.Item key="excluir" href={`${id}/excluir`}>
                <Menu.Item.Icon>
                    <Trash2Icon className="text-red-500" />
                </Menu.Item.Icon>
                <Menu.Item.Text>Excluir</Menu.Item.Text>
            </Menu.Item>,
        ],
    },
]

export default function TrocaAutomaticaHorario() {
    const [[dataInicial, dataFinal], setPeriodo] = usePeriodo()
    const empresaId = useStore(state => state.companyId)
    const { data = [], isLoading } = trocaHorario.trocaAutomatica.useFetch({
        queryParams: {
            dataInicial,
            dataFinal,
            empresaId,
        },
    })

    const useFilter = useFilterLocaisCentroStore()
    const rows = data.filter(useFilter)

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={[
                        'Manutenção do ponto',
                        'Troca automática de horário',
                    ]}
                >
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    >
                        <SP.Root
                            onChange={setPeriodo}
                            value={[dataInicial, dataFinal]}
                            allowedTypes={['competencia', 'periodo']}
                        >
                            <SP.DateSelector />
                        </SP.Root>
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    onAdd
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    ToolbarProps={{
                        exportButtonProps: {
                            fileName: `exportacao-troca-automatica`,
                        },
                    }}
                />
            </ContentGuide.Card>
            <Outlet context={{ data }} />
        </ContentGuide>
    )
}

export function useTrocaAutomaticaHorarioOutletContext() {
    return useOutletContext<{ data: TrocaAutomaticaTabela[] }>()
}
